import { useEffect, useRef, useState } from 'react';
import { span, typed, useReducedMotion, useScrollProgress } from '../motion.ts';

/**
 * The problem, told the way the film tells it — and in the film's own words.
 *
 * It is one tall section holding one sticky stage. Scrolling moves a single
 * number, and everything on the stage is a function of that number: which
 * sentence is up, how much of the question has been typed, which day the
 * calendar is on, which slide of the standard tour. Nothing is on a timer, so
 * scrolling back un-says it, and nothing hijacks the scroll — it is the page's
 * own scrolling, with a stage that stays put while it happens.
 *
 * The visual is built at a fixed 560 x 440 and scaled to the column (the same
 * way the Studio panel is), so positions can be written once, in pixels.
 *
 * It is the *problem* — what a buyer gets today — so it is titled and labelled as
 * that: a real section title ("Why we built it"), an eyebrow that says whose story
 * this is ("The problem we saw", then "What we built"), and a tag over the picture
 * ("Today, without Persivo" until the last beat). Without them the calendar and the
 * standard tour read as parts of our product.
 *
 * With reduced motion it is the same five sentences as a plain list.
 */
const BEATS = [
  { lead: 'Every buyer is different.', em: 'Every demo is the same.' },
  { lead: 'A buyer arrives with', em: 'one question.' },
  { lead: 'And gets a', em: 'calendar link.' },
  { lead: 'Days later, someone walks them through the standard tour.', em: 'The question never comes up.' },
  { lead: 'So we built', em: 'Persivo.', sub: 'To personalize. And to perceive.' },
] as const;

const QUESTION = 'What happens after signup?';
const OTHERS = ['Is it secure for our team?', 'How does pricing scale?', 'Does it connect to our data?'];
const DAYS = [[28, 29, 30, 1, 2, 3, 4], [5, 6, 7, 8, 9, 10, 11], [12, 13, 14, 15, 16, 17, 18], [19, 20, 21, 22, 23, 24, 25]];
const SLIDES = ['Welcome', 'Platform overview', 'Integrations', 'Pricing', 'Next steps'];
const ROWS = [['Workspace', 100], ['Data source', 82], ['First report', 64], ['Shared', 46]] as const;
const W = 560, H = 440, TAG = 46;                          // the label above the picture

export function Story() {
  const pin = useRef<HTMLElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const p = useScrollProgress(pin);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(1, el.clientWidth / W));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [reduced]);

  if (reduced) {
    return <section className="story story-static" id="story" aria-labelledby="story-title">
      <div className="wrap">
        <p className="eyebrow">The problem we saw</p>
        <h2 className="section-title" id="story-title">Why we built it</h2>
        <ol>{BEATS.map(b => <li key={b.em}><span>{b.lead}</span> <em>{b.em}</em>{'sub' in b && <small>{b.sub}</small>}</li>)}</ol>
      </div>
    </section>;
  }

  const n = BEATS.length;
  const beat = Math.min(n - 1, Math.floor(p * n));
  const local = p * n - beat;                                   // 0..1 inside the current beat
  const q = beat === 1 ? span(local, 0.12, 0.78) : beat > 1 ? 1 : 0;
  const day = beat === 2 ? Math.min(3, Math.floor(span(local, 0.35, 0.95) * 4)) : beat > 2 ? 3 : 0;
  const slide = beat === 3 ? Math.min(SLIDES.length - 1, Math.floor(span(local, 0.1, 0.9) * SLIDES.length)) : beat > 3 ? SLIDES.length - 1 : 0;

  return <section className="story" id="story" ref={pin} aria-labelledby="story-title" data-beat={beat} style={{ ['--beats' as string]: n }}>
    <div className="story-stage">
      <div className="story-field" aria-hidden="true"><i/><i/><i/></div>
      <div className="wrap story-grid">
        <div className="story-copy">
          <p className="eyebrow story-eyebrow"><span key={beat < n - 1 ? 'problem' : 'answer'}>{beat < n - 1 ? 'The problem we saw' : 'What we built'}</span></p>
          <h2 className="section-title story-title" id="story-title">Why we built it</h2>
          <div className="story-lines" aria-hidden="true">
            {BEATS.map((b, i) => (
              <p key={b.em} className="story-line" data-on={i === beat ? '' : undefined} data-past={i < beat ? '' : undefined} >
                <span>{b.lead}</span> <em>{b.em}</em>
                {'sub' in b && <small>{b.sub}</small>}
              </p>
            ))}
          </div>
          <div className="story-rail" aria-hidden="true">{BEATS.map((b, i) => <i key={b.em} data-state={i < beat ? 'done' : i === beat ? 'now' : 'next'}><b style={{ ['--f' as string]: i < beat ? 1 : i === beat ? local : 0 }}/></i>)}</div>
          <p className="story-hint" aria-hidden="true" data-hide={beat > 0 || p > 0.01 ? '' : undefined}>Scroll</p>
        </div>

        <div className="story-visual" ref={box} aria-hidden="true" style={{ height: H * scale + TAG }}>
          <p className="story-tag" data-with={beat === n - 1 ? '' : undefined}><i/><span key={beat === n - 1 ? 'with' : 'today'}>{beat === n - 1 ? 'With Persivo' : 'Today, without Persivo'}</span></p>
          <div className="sv" style={{ width: W, height: H, transform: `scale(${scale})` }} data-beat={beat}>
            {/* 0 — three buyers, one demo */}
            {OTHERS.map((t, i) => <div key={t} className={`sv-pill sv-alt sv-alt${i}`}><span className="hs-avatar">{'MRS'[i]}</span><span className="sv-pq">{t}</span></div>)}
            <div className="sv-std"><small>THE STANDARD DEMO</small><b>30 minutes</b><u/><u/><u/></div>
            <i className="sv-arrow sv-arrow0"/><i className="sv-arrow sv-arrow1"/><i className="sv-arrow sv-arrow2"/>

            {/* 1+ — the question */}
            <div className="sv-pill sv-main"><span className="hs-avatar">M</span><span className="sv-pq">{typed(QUESTION, q)}{beat === 1 && q < 1 && <i className="hs-caret"/>}</span><span className="hs-send"><svg viewBox="0 0 16 16"><path d="M8 13V3M3.5 7.5 8 3l4.5 4.5"/></svg></span><em className="sv-unanswered">still unanswered</em></div>

            {/* 2 — a calendar link */}
            <div className="sv-cal">
              <header><b>Book a 30-minute intro</b><span>Thursday</span></header>
              <div className="sv-days">{DAYS.flat().map((d, i) => <span key={i} className={i < 3 ? 'o' : ''}>{d}</span>)}<i className="sv-hl" style={{ ['--col' as string]: 3 + day, ['--row' as string]: 1 }}><b>{DAYS[1]![3 + day] ?? ''}</b></i></div>
              <footer>{['9:00', '10:30', '2:00'].map(t => <span key={t}>{t}</span>)}</footer>
            </div>

            {/* 3 — the standard tour */}
            <div className="sv-tour">
              <header><b>The standard tour</b><span>slide {slide + 1} of {SLIDES.length}</span></header>
              <ul>{SLIDES.map((s, i) => <li key={s} data-state={i < slide ? 'done' : i === slide ? 'now' : 'next'}><i>{i < slide ? '✓' : ''}</i>{s}</li>)}</ul>
            </div>

            {/* 4 — the answer */}
            <i className="sv-wire"/>
            <img className="sv-orb" src="/agent-thinking.svg" alt="" width="150" height="150"/>
            <div className="sv-answer">
              <div className="hs-answer-head"><span>PERSIVO</span><span>Activation <b>·</b> example data</span></div>
              <p>Let me show you the onboarding journey.</p>
              <div className="hs-rows">{ROWS.map(([label, v], i) => <div key={label} style={{ ['--i' as string]: i }}><span>{label}</span><i><b style={{ ['--w' as string]: v + '%' }}/></i><em>{v}%</em></div>)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="story-edge" aria-hidden="true"/>
    </div>
    <ol className="sr-only">{BEATS.map(b => <li key={b.em}>{b.lead} {b.em}{'sub' in b ? ' ' + b.sub : ''}</li>)}</ol>
  </section>;
}
